/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: hooks/useAnnouncementList.ts
 🗓️ Created: 2025-01-15
 🧩 Description:
    Custom hook for managing Announcements list.
    Integrates with useDataGrid for pagination, sorting, and search.
    Supports Domain Contextual Filtering pattern.

 🔁 Last Updated: 2025-01-03 by Rodrigo Cuadra (Added Domain Filtering)
────────────────────────────────────────────────────────────────*/

import { useCallback, useMemo, useEffect } from 'react';
import { useI18n } from '../../../hooks/useI18n';
import { useDataGrid } from '../../../hooks/useDataGrid';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useContextStore } from '../../../store/context';
import { fetchAnnouncements } from '../api/announcementApi';
import { Announcement } from '../types';

interface UseAnnouncementListOptions {
  domainId?: number;
  onRefreshNeeded?: () => void;
}

/**
 * Hook for managing Announcements list
 * Implements Domain Contextual Filtering pattern
 */
export function useAnnouncementList(options: UseAnnouncementListOptions = {}) {
  const { t } = useI18n();
  const { showError } = useNotifications();
  const { selectedDomain } = useContextStore();

  // Use provided domainId or fallback to selected domain
  const effectiveDomainId = options.domainId ?? selectedDomain?.id;

  const fetchAnnouncementsData = useCallback(
    async (params: {
      page: number;
      limit: number;
      sortBy: string;
      sortOrder: 'asc' | 'desc';
      search?: string;
    }) => {
      try {
        const result = await fetchAnnouncements(
          {
            page: params.page,
            limit: params.limit,
            sortBy: params.sortBy,
            sortOrder: params.sortOrder,
            domainId: effectiveDomainId,
          },
          params.search,
          params.sortBy,
        );

        return {
          data: result.data as Announcement[],
          pagination: result.pagination,
        };
      } catch (error) {
        const message =
          error instanceof Error
            ? error.message
            : t('announcements.messages.loadError');
        showError(message);
        return {
          data: [] as Announcement[],
          pagination: {
            page: params.page,
            limit: params.limit,
            total: 0,
            totalPages: 0,
          },
        };
      }
    },
    [showError, t, effectiveDomainId],
  );

  const availableColumns = useMemo(
    () => [
      {
        key: 'name',
        label: t('announcements.columns.name'),
        visible: true,
        sortable: true,
      },
      {
        key: 'recording',
        label: t('announcements.columns.recording'),
        visible: true,
        sortable: false,
      },
      {
        key: 'destination',
        label: t('announcements.columns.destination'),
        visible: true,
        sortable: false,
      },
      {
        key: 'enabled',
        label: t('announcements.columns.enabled'),
        visible: true,
        sortable: true,
      },
      {
        key: 'createdAt',
        label: t('common:createdAt'),
        visible: false,
        sortable: true,
      },
      {
        key: 'actions',
        label: t('common:actions'),
        visible: true,
        sortable: false,
      },
    ],
    [t],
  );

  const {
    data,
    loading,
    columns,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
    refresh,
  } = useDataGrid({
    fetchData: fetchAnnouncementsData,
    initialColumns: availableColumns,
    initialSortBy: 'name',
    initialSortOrder: 'asc',
    initialLimit: 10,
    moduleName: 'announcements',
    reloadKey: effectiveDomainId ?? null,
  });

  // Trigger refresh when domainId changes
  useEffect(() => {
    if (effectiveDomainId !== undefined && options.onRefreshNeeded) {
      options.onRefreshNeeded();
    }
  }, [effectiveDomainId]);

  return {
    data: data as Announcement[],
    loading,
    columns,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
    refresh,
    effectiveDomainId,
  };
}

